import { useScrollContent } from '@exam-management/common/hooks';
import { type QuestionStatus } from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { Button, Flex } from '@shared/components';
import { WithPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { type QContentDetailFormContextData } from '../../types';

import { HIDDEN_STATUSES } from './constants';

interface QContentDetailFormActionsProps {
  onSubmit: (data: QContentDetailFormContextData) => Promise<void>;
  onSubmitAndCreateNew: (data: QContentDetailFormContextData) => Promise<void>;
  questionId?: string;
  questionStatus: QuestionStatus;
}

export default function QContentDetailFormActions(props: Readonly<QContentDetailFormActionsProps>) {
  const { onSubmit, onSubmitAndCreateNew, questionStatus } = props;

  const { scrollTo } = useScrollContent();
  const [saveMethod, setSaveMethod] = useState<'save' | 'saveAndCreateNew'>('save');
  const formContext = useFormContext<QContentDetailFormContextData>();
  const { isDirty, isSubmitting } = formContext.formState;
  const { showSnackbar } = useSnackbar();

  const handleShowRequiredFieldsError = () => {
    showSnackbar({
      message: 'Please complete all required fields',
      variant: 'error',
    });
  };

  const handleClickSave = async (data: QContentDetailFormContextData) => {
    setSaveMethod('save');
    await onSubmit(data);
  };

  const handleClickSaveAndNew = async (data: QContentDetailFormContextData) => {
    setSaveMethod('saveAndCreateNew');
    await onSubmitAndCreateNew(data);
    scrollTo({ x: 0, y: 0 });
  };

  if (HIDDEN_STATUSES.includes(questionStatus)) return null;

  return (
    <Flex sx={{ gap: (theme) => theme.spacing(6), width: '100%' }}>
      <Button
        size='large'
        sx={{ width: '146px' }}
        onClick={formContext.handleSubmit(handleClickSave, handleShowRequiredFieldsError)}
        loading={isSubmitting && saveMethod === 'save'}
        disabled={!isDirty || isSubmitting}
      >
        Save
      </Button>
      <WithPermission requiredPermission={[APP_PERMISSIONS.QBANK.QUESTION.CREATE]}>
        <Button
          size='large'
          variant='outlined'
          color='secondary'
          onClick={formContext.handleSubmit(handleClickSaveAndNew, handleShowRequiredFieldsError)}
          loading={isSubmitting && saveMethod === 'saveAndCreateNew'}
          disabled={!isDirty || isSubmitting}
        >
          Save & Create New
        </Button>
      </WithPermission>
    </Flex>
  );
}
